import { TuiFloatingContainer, TuiSlides } from '@taiga-ui/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { CreatorType, Patient } from '@pixacare/domain';
import { Telemonitoring } from '@pixacare/dtos';
import { AppConfigHttpService } from '@services/http/app-config.http.service';
import { LocalStorageService } from '@services/local-storage.service';
import { ActionState } from '@shared/models/helpers/action-state';
import { UserInputSelection } from '@shared/models/helpers/user-input-selection';
import {
  defaultReminderDaysInterval,
  telemonitoringRequestStepsConfig,
} from '@shared/models/telemonitoring/telemonitoring-request.config';
import { TuiButton, TuiDialogContext } from '@taiga-ui/core';
import { TuiStepper } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { Observable } from 'rxjs';
import { TelemonitoringRequestStep } from 'src/app/shared/models/enums/telemonitoring-request-step.enum';
import { DEFAULT_LOCALE_CODE } from 'transloco.config';
import { TelemonitoringNamePipe } from '../pipes/telemonitoring-name.pipe';
import { TelemonitoringCollaboratorsComponent } from '../telemonitoring-collaborators/telemonitoring-collaborators.component';
import { TelemonitoringRequestDetailsComponent } from '../telemonitoring-request-details/telemonitoring-request-details.component';
import { TelemonitoringRequestLocationComponent } from '../telemonitoring-request-location/telemonitoring-request-location.component';
import { TelemonitoringRequestResultComponent } from '../telemonitoring-request-result/telemonitoring-request-result.component';
import { TelemonitoringRequestSequencesComponent } from '../telemonitoring-request-sequences/telemonitoring-request-sequences.component';
import { TelemonitoringRequestTypeComponent } from '../telemonitoring-request-type/telemonitoring-request-type.component';
import { TelemonitoringRequestForm } from './telemonitoring-request-form';
import { TelemonitoringRequestProps } from './telemonitoring-request-props';
import { TelemonitoringRequestService } from './telemonitoring-request.service';
import {
  validateDepartmentIds,
  validateLocaleCode,
  validatePatientContact,
  validatePatientForm,
} from './telemonitoring-request.validators';
import { SlidesScrollResetDirective } from '@modules/shared/directives/slides-scroll-reset.directive';

const SHOULD_SEND_TEXT_MESSAGE_TO_PATIENT_KEY = 'telemonitoring-should-send-text-message-to-patient';
const ENABLE_AUTOMATIC_REMINDER_FOR_INACTIVE_PATIENT = 'telemonitoring-enable-automatic-reminder-for-inactive-patient';

@Component({
  templateUrl: './telemonitoring-request.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-1 flex-col' },
  imports: [
    SlidesScrollResetDirective,
    CommonModule,
    TuiStepper,
    TuiFloatingContainer,
    TuiSlides,
    TuiButton,
    FormsModule,
    ReactiveFormsModule,
    TelemonitoringRequestTypeComponent,
    TelemonitoringRequestDetailsComponent,
    TelemonitoringRequestSequencesComponent,
    TelemonitoringRequestLocationComponent,
    TelemonitoringRequestResultComponent,
    TelemonitoringCollaboratorsComponent,
    TranslocoDirective,
  ],
  providers: [TelemonitoringRequestService, TelemonitoringNamePipe],
})
export class TelemonitoringRequestComponent implements OnInit {
  private readonly telemonitoringNamePipe = inject(TelemonitoringNamePipe);
  private readonly telemonitoringRequestService = inject(TelemonitoringRequestService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly configuration = inject(AppConfigHttpService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly context = inject<TuiDialogContext<void, TelemonitoringRequestProps>>(POLYMORPHEUS_CONTEXT);
  private readonly translocoService = inject(TranslocoService);

  slideDirection = 0;
  private currentPageIndex = 0;

  get activePageIndex(): number {
    return this.currentPageIndex;
  }

  set activePageIndex(index: number) {
    this.slideDirection = index - this.currentPageIndex;
    this.currentPageIndex = index;
  }
  isLoading = false;
  defaultName: string = null;

  form: FormGroup;

  TelemonitoringRequestStep = TelemonitoringRequestStep;
  telemonitoringRequestStepsConfig = telemonitoringRequestStepsConfig;

  state$: Observable<ActionState<Telemonitoring>>;

  get patient(): Patient {
    return this.context.data.patient;
  }

  get patientId(): number {
    return this.context.data.patient.id;
  }

  get departmentIds(): number[] {
    return this.context.data.departmentIds;
  }

  get clientCode(): string {
    return this.context.data.clientCode;
  }

  get shouldSendTextMessageToPatient(): boolean {
    return this.form.get('shouldSendTextMessageToPatient').value;
  }

  get enableAutomaticReminderForInactivePatient(): boolean {
    return this.form.get('enableAutomaticReminderForInactivePatient').value;
  }

  get reminderDaysInterval(): number {
    return this.form.get('reminderDaysInterval').value;
  }

  ngOnInit(): void {
    this.defaultName = this.telemonitoringNamePipe.getTelemonitoringDefaultName(this.patient);

    const currentUser = this.authenticationService.user;

    const selectedCollaborator: UserInputSelection = {
      user: {
        id: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        medicalSpecialtyLabel: this.translocoService.translate('Vous'),
        clientNames: null,
        liveId: null,
      },
    };

    this.form = new FormGroup<TelemonitoringRequestForm>({
      name: new FormControl('', [Validators.maxLength(256)]),
      patientId: new FormControl(this.patient.id, [Validators.required]),
      clientCode: new FormControl(this.clientCode, [Validators.required]),
      departmentIds: new FormControl(this.departmentIds),
      sequences: new FormControl(this.context.data.sequences),
      message: new FormControl(),
      collaborators: new FormControl([selectedCollaborator], [Validators.minLength(1)]),
      localeCode: new FormControl(this.patient.localeCode ?? DEFAULT_LOCALE_CODE),
      patientContact: new FormControl(),
      patientForm: new FormControl(),
      creatorType: new FormControl(CreatorType.BY_USER, [Validators.required]),
      shouldSendTextMessageToPatient: new FormControl(
        (this.localStorage.get(SHOULD_SEND_TEXT_MESSAGE_TO_PATIENT_KEY) ?? 'true') === 'true',
      ),
      enableAutomaticReminderForInactivePatient: new FormControl(
        (this.localStorage.get(ENABLE_AUTOMATIC_REMINDER_FOR_INACTIVE_PATIENT) ?? 'true') === 'true',
      ),
      reminderDaysInterval: new FormControl(defaultReminderDaysInterval, [Validators.min(1)]),
    });

    // add dynamic validators
    this.form.get('departmentIds').addValidators(validateDepartmentIds(this.form));
    this.form
      .get('patientContact')
      .addValidators(
        validatePatientContact(this.form, this.configuration.configuration.inputValidation.phoneNumber.regex),
      );
    this.form.get('localeCode').addValidators(validateLocaleCode(this.form));
    this.form.get('patientForm').addValidators(validatePatientForm(this.form));

    // we want the error message to be shown directly on departmentIds
    this.form.get('departmentIds').markAsTouched();
  }

  isStepValid(fields: string[]): boolean {
    if (!fields) {
      return true;
    }
    return fields.every((field) => this.form.get(field).valid);
  }

  pickCreatorType(type: CreatorType): void {
    this.form.get('creatorType').setValue(type);
    this.nextPage();
  }

  previousPage(): void {
    if (this.activePageIndex === 0) {
      this.context.completeWith();
    } else {
      this.activePageIndex--;
    }
  }

  nextPage(): void {
    if (this.activePageIndex === telemonitoringRequestStepsConfig.length - 1) {
      this.createTelemonitoring();
    } else {
      this.activePageIndex++;
    }
  }

  createTelemonitoring(): void {
    this.localStorage.set(SHOULD_SEND_TEXT_MESSAGE_TO_PATIENT_KEY, this.shouldSendTextMessageToPatient.toString());
    this.localStorage.set(
      ENABLE_AUTOMATIC_REMINDER_FOR_INACTIVE_PATIENT,
      this.enableAutomaticReminderForInactivePatient.toString(),
    );
    this.state$ = this.telemonitoringRequestService.createMonitoring(this.form);
    this.isLoading = true;
  }

  closeDialog(): void {
    this.context.completeWith();
  }
}
